class AccountNavigation {
    private readonly navigationContainer: HTMLElement | null;
    private radios: NodeListOf<HTMLInputElement>;

    constructor() {
        this.navigationContainer = document.querySelector('[data-account-navigation]');
        this.radios = this.navigationContainer
            ? this.navigationContainer.querySelectorAll('input[type=radio][data-setting-panel]')
            : document.createDocumentFragment().childNodes as NodeListOf<HTMLInputElement>;

        this.init();
    }

    /**
     * Initializes event listeners and syncs UI with the URL or default state.
     */
    private init(): void {
        if (!this.navigationContainer) {
            console.warn('AccountNavigation: Navigation container not found.');
            return;
        }

        this.radios.forEach((radio) => {
            radio.addEventListener('change', () => this.onRadioChange(radio));
        });

        window.addEventListener('popstate', () => this.syncWithUrl());

        this.syncWithUrl();
    }

    /**
     * Handles radio button change event, triggers AJAX, updates the URL, and updates the title.
     * @param radio - The selected radio input element.
     */
    private onRadioChange(radio: HTMLInputElement): void {
        const panelCode = radio.dataset.settingPanel;

        if (!panelCode) {
            console.warn('AccountNavigation: No setting panel code found.');
            return;
        }

        const panelTitle = radio.dataset.panelTitle || '';

        this.updatePanel(panelCode, panelTitle, true);
    }

    /**
     * Updates the URL, triggers an AJAX request to switch panels, and updates the page title.
     * @param panelCode - The active setting panel code.
     * @param panelTitle - The title to set in the document.
     * @param pushHistory - Whether to push a new entry to browser history.
     */
    private updatePanel(panelCode: string, panelTitle: string, pushHistory: boolean): void {
        // Get the current URL
        const currentUrl = window.location.pathname;

        // Split the URL by '/', remove the last part, and append the new part
        const parts = currentUrl.split('/');

        // Replace the second segment (index 2)
        parts[2] = panelCode;
        
        // Rebuild the new URL (removes empty strings)
        const newUrl = '/' + parts.filter(Boolean).join('/');

        // Only push new history entry if it's a user-triggered change
        if (pushHistory)
            history.pushState({panel: panelCode}, '', newUrl);

        // Update the document title
        this.updatePageTitle(panelTitle);

        // Perform AJAX request to update panel content
        window.oc.request(this.navigationContainer, 'onSwitchAccountSettingPanel', {
            data: {panel: panelCode},
            flash: true,
            loading: `[data-setting-panel="${panelCode}"] + .sidebar-item .loading-icon`,
        });
    }

    /**
     * Updates the active radio input, URL, and title based on the current page state.
     */
    private syncWithUrl(): void {
        const pathSegments = window.location.pathname.split('/');
        const activeCode = pathSegments.length > 2 ? pathSegments[2] : null;

        let selectedRadio: HTMLInputElement | null = null;

        this.radios.forEach((radio) => {
            if (radio.dataset.settingPanel === activeCode) {
                selectedRadio = radio;
            }
        });

        // If no panel is set in the URL, use the default one
        if (!selectedRadio)
            selectedRadio = this.navigationContainer?.querySelector('input[type=radio][data-default-panel="true"]') as HTMLInputElement;

        if (selectedRadio) {
            selectedRadio.checked = true;

            // Get title from data attribute
            const panelTitle = selectedRadio.dataset.panelTitle || '';

            // Update the page title & AJAX content when navigating back/forward
            this.updatePageTitle(panelTitle);
            this.updatePanel(selectedRadio.dataset.settingPanel!, panelTitle, false);
        }
    }

    /**
     * Updates the document title, replacing the part before the pipe "|".
     * @param panelTitle - The new title for the setting panel.
     */
    private updatePageTitle(panelTitle: string): void {
        if (!panelTitle) return;

        const currentTitle = document.title;
        const titleParts = currentTitle.split('|');

        // Replace the first part of the title and keep the existing suffix
        document.title = panelTitle + (titleParts.length > 1 ? ' | ' + titleParts.slice(1).join('|') : '');
    }
}

// Initialize the class when the DOM is ready
document.addEventListener('DOMContentLoaded', () => new AccountNavigation());